// Universal entity link. Every clickable name in the app routes through
// this so the link mapping (matter → /matter/:eID, judge → /judge/:id, …)
// lives in one place.
//
// Routes assumed to exist (some land in sibling agent PRs B/C/E — they
// resolve once those merge to main):
//   matter        → /matter/:eID
//   client        → /client/:id
//   judge         → /judge/:id
//   opposing      → /opposing/:id
//   team          → /team/:userId
//   practiceArea  → /practice/:id
//   tag           → /tag/:id
//   relative      → /client/:clientId?tab=relatives  (relatives don't get
//                   their own page — link to the client's relatives tab)
//
// Visual: `cc-link` underline-on-hover + brand color. Use `subtle` when the
// surrounding text color (muted strip, accent chip) should win until hover.

import { Link } from "react-router-dom";
import type { ReactNode } from "react";

export type EntityKind =
  | "matter"
  | "client"
  | "judge"
  | "opposing"
  | "team"
  | "practiceArea"
  | "tag"
  | "relative";

interface BaseProps {
  kind: EntityKind;
  id: string;
  /** Required when kind === "matter": the human-facing eID used in the URL. */
  eID?: string;
  /** Required when kind === "relative": the client owning this relative. */
  clientId?: string;
  children: ReactNode;
  className?: string;
  ariaLabel?: string;
  /** When true, link inherits parent color until hover. Use inside muted
      metadata strips or accent-colored chips. */
  subtle?: boolean;
  /** Stop click bubbling — needed when the link sits inside a row that has
      its own onClick navigate. Without this both fire. */
  stopPropagation?: boolean;
  title?: string;
  style?: React.CSSProperties;
}

function routeFor(props: BaseProps): string | null {
  switch (props.kind) {
    case "matter":
      // Prefer eID (human-facing) but fall back to id if that's all we have.
      return `/matter/${encodeURIComponent(props.eID ?? props.id)}`;
    case "client":
      return `/client/${encodeURIComponent(props.id)}`;
    case "judge":
      return `/judge/${encodeURIComponent(props.id)}`;
    case "opposing":
      return `/opposing/${encodeURIComponent(props.id)}`;
    case "team":
      return `/team/${encodeURIComponent(props.id)}`;
    case "practiceArea":
      return `/practice/${encodeURIComponent(props.id)}`;
    case "tag":
      return `/tag/${encodeURIComponent(props.id)}`;
    case "relative":
      // Relatives are embedded inside Case docs (no individual ID), so the
      // canonical destination is the relatives tab on the parent client.
      // The relative's own composite key (caseId+index) can be encoded via
      // a hash if desired by the consuming page.
      if (!props.clientId) return null;
      return `/client/${encodeURIComponent(props.clientId)}?tab=relatives#${encodeURIComponent(props.id)}`;
  }
}

export function EntityLink(props: BaseProps) {
  const to = routeFor(props);
  const cls = (props.subtle ? "cc-link-subtle " : "cc-link ") + (props.className ?? "");

  // If we couldn't compute a route (e.g. relative with no clientId, missing
  // id), render the children as plain text instead of a broken link. Better
  // a dead label than a broken one.
  if (!to) {
    return (
      <span className={props.className} style={props.style} title={props.title}>
        {props.children}
      </span>
    );
  }

  return (
    <Link
      to={to}
      className={cls.trim()}
      aria-label={props.ariaLabel}
      title={props.title}
      style={props.style}
      onClick={
        props.stopPropagation
          ? (e) => {
              e.stopPropagation();
            }
          : undefined
      }
    >
      {props.children}
    </Link>
  );
}
